export const supportedLanguages = ['en', 'fr'];
export const defaultLanguage = 'en';

const sharedSkills = [
  'Flutter',
  'Android',
  'iOS',
  'Kotlin',
  'Python',
  'Swift',
  'KMP',
  'Node.js',
  'Ktor',
  'OpenAPI',
  'EUDI',
  'OpenID4VCI',
  'OpenID4VP',
  'Verifiable Credentials',
  'TypeScript',
  'Docker',
  'CI/CD',
  'Clean Architecture',
];

export const localizedContent = {
  en: {
    head: {
      title: 'Hedi Hidouri | Senior Mobile Engineer',
      description:
        'Senior Mobile Engineer working on iOS, Android, Flutter, KMP, and Ktor backend services.',
      locale: 'en_US',
      keywords: [
        'Senior Mobile Engineer',
        'Digital Identity Engineer',
        'Flutter Developer',
        'Android Developer',
        'iOS Developer',
        'Kotlin Multiplatform',
        'EUDI',
        'EUDIW',
        'OIDC4VCI',
        'OIDC4VP',
        'OpenID4VCI',
        'OpenID4VP',
        'W3C Verifiable Credentials',
        'eIDAS 2.0',
        'Age Verification',
        'OpenAPI',
        'Python',
        'Docker',
        'Mobile Architecture',
        'BFF',
        'CI/CD Mobile',
        'Freelance Mobile Engineer Paris',
      ],
    },
    layout: {
      skipToContent: 'Skip to Content',
      whatsAppCta: 'WhatsApp',
      whatsAppAriaLabel: 'Open WhatsApp chat',
    },
    nav: {
      homeAriaLabel: 'Home',
      menuButtonAriaLabel: 'Menu',
      languageSwitcherAriaLabel: 'Select language',
      links: [
        { name: 'About', url: '#about' },
        { name: 'Experience', url: '#jobs' },
        { name: 'Expertise', url: '#services' },
        { name: 'CV', url: '#cv' },
        { name: 'Contact', url: '#contact' },
      ],
    },
    hero: {
      intro: 'Hi, my name is',
      title: 'Hedi Hidouri.',
      subtitle: 'Senior Mobile Engineer.',
      description:
        'I design, build, and modernize robust mobile products for tech, fintech, and e-commerce teams. My focus is simple: secure Android/iOS delivery, durable architecture, and reliable execution from MVP to scale.',
      quickStats: [
        { label: 'Location', value: 'Paris, France' },
        { label: 'Experience', value: '8+ years' },
      ],
      cta: 'Let\'s Work Together',
    },
    about: {
      heading: 'About Me',
      paragraphs: [
        'I support tech, fintech, and e-commerce companies in designing, building, and modernizing reliable mobile applications that are built to last.',
        'Across high-scale and high-constraint products (logistics, payments, digital identity, mobility, banking), I help teams deliver robust Android/iOS apps, secure critical user journeys, and structure maintainable mobile + backend architectures.',
      ],
      valueTitle: 'What I Bring',
      valueList: [
        'End-to-end ownership: mobile, backend (BFF), architecture, CI/CD.',
        'Strong expertise in security-sensitive systems and compliance constraints.',
        'Ability to frame products technically from MVP stage to large-scale platforms.',
        'Pragmatic delivery mindset focused on production outcomes.',
      ],
      techIntro: 'Core technologies I use regularly:',
      skills: sharedSkills,
      snapshotTitle: 'Profile Snapshot',
      snapshotStats: [
        { label: 'Role', value: 'Senior Mobile Engineer' },
        { label: 'Location', value: 'Paris, France (hybrid / remote)' },
        { label: 'Languages', value: 'French, English' },
        {
          label: 'Preferred Environments',
          value: 'Product teams, Agile delivery, high-impact mobile platforms',
        },
      ],
      domains: ['Fintech & Banking', 'E-commerce', 'Retail', 'Digital Platforms', 'Services IT'],
    },
    jobs: {
      heading: 'Professional Experience',
      tabsAriaLabel: 'Job tabs',
      items: [
        {
          title: 'Senior Mobile Engineer',
          company: 'INGroupe',
          location: 'Paris, France',
          range: 'Apr 2025 - Present',
          url: 'https://ingroupe.com/',
          points: [
            'Developing a cross-platform digital identity wallet aligned with EUDI wallet reference architecture on Android (Kotlin, Jetpack Compose) and iOS (Swift, SwiftUI).',
            'Bootstrapped the application from scratch with modular Clean Architecture, MVI patterns, and secure identity handling flows.',
            'Designed and implemented identity SDK capabilities for credential provisioning via NFC (ISO-DEP) and QR code, plus identity derivation/authentication with X.509, PKCS#12, and HOTP.',
            'Designed and developed backend issuer services for credential issuance using OpenID4VCI, with secure, standards-aligned API contracts.',
            'Implemented EUDI-compatible credential protocols, including OIDC4VCI/OpenID4VCI (issuance), OIDC4VP/OpenID4VP (presentation), and SD-JWT selective disclosure/signature validation.',
            'Developed and evolved BFF services to simplify mobile integration, orchestrate identity/payment flows, and reduce client-side complexity.',
            'Delivered age-verification and digital badging use cases, including Badgemeup, an EUDI-compliant badging and credential issuance app.',
            'Built and maintained mobile CI/CD pipelines with Bitbucket, Jenkins, Fastlane, Docker, and automated quality gates (JUnit, XCTest, MockK).',
            'Delivered end-to-end identity-sharing workflows aligned with eIDAS 2.0 requirements while collaborating on architecture decisions, cross-team coordination, and technical mentoring.',
          ],
        },
        {
          title: 'Senior Mobile Engineer',
          company: 'Stuart',
          location: 'Paris, France',
          range: 'Nov 2022 - Apr 2025',
          url: 'https://stuart.com/',
          points: [
            'Led the modernization of Android and iOS user experiences with Flutter for a high-scale logistics platform.',
            'Developed and supervised a mobile Backend For Frontend in Node.js with endpoints tailored for app needs.',
            'Orchestrated backend components with Docker and Kafka to improve reliability between services and apps.',
            'Structured and scaled CI/CD pipelines with GitHub Actions and Fastlane for automated build and release flows.',
            'Raised software quality with unit and integration testing in an Agile delivery context.',
            'Contributed to core functional domains including dispatch optimization, delivery tracking, and payment evolution.',
          ],
        },
        {
          title: 'Mobile Development Consultant',
          company: 'Carrefour',
          location: 'Paris, France',
          range: 'May 2021 - Nov 2022',
          url: 'https://www.carrefour.com/',
          points: [
            'Contributed to large-scale evolution of Carrefour Drive and Delivery Android applications.',
            'Delivered new features and UI/UX refactors to improve fluidity, performance, and customer experience.',
            'Modernized architecture using MVI and Clean Architecture with reactive stacks (Flow, Coroutines).',
            'Introduced dependency injection and modularization practices with Hilt for better maintainability.',
            'Improved mobile CI through Jenkins and Gradle Kotlin DSL (KTS) automation.',
            'Reinforced reliability with unit and integration tests while collaborating closely with product and design teams.',
          ],
        },
        {
          title: 'Mobile Android / iOS Engineer',
          company: 'JCDecaux',
          location: 'Paris, France',
          range: 'Jan 2021 - May 2021',
          url: 'https://www.jcdecaux.com/',
          points: [
            'Developed and continuously improved bike-sharing mobile apps used by over one million users.',
            'Implemented new Android and iOS features while optimizing overall app performance.',
            'Reduced production incidents through targeted stability and bug-fix initiatives.',
            'Implemented unit and integration tests for Android and iOS, achieving strong coverage.',
            'Worked in close collaboration with product teams to clarify business needs and improve UX outcomes.',
            'Delivered in Agile/Scrum cycles with active planning and execution ownership.',
          ],
        },
        {
          title: 'iOS Mobile Engineer',
          company: 'HSBC',
          location: 'Paris, France',
          range: 'Mar 2019 - Dec 2020',
          url: 'https://www.hsbc.com/',
          points: [
            'Enhanced the iOS banking application with continuous UX and functional improvements.',
            'Implemented advanced security capabilities, including biometric authentication and wallet provisioning.',
            'Translated business and compliance requirements into scalable technical solutions.',
            'Delivered unit and integration testing and supported internal acceptance testing phases.',
            'Investigated and resolved production incidents to improve app stability and quality of service.',
            'Contributed to internal library enrichment, documentation, and engineering best-practice initiatives.',
          ],
        },
        {
          title: 'Mobile Software Engineer',
          company: 'Bulldozer',
          location: 'Ariana, Tunisia',
          range: 'Jul 2017 - Feb 2019',
          url: 'https://www.linkedin.com/company/bulldozer/',
          points: [
            'Contributed to the full mobile lifecycle: planning, development, testing, and release on Google Play and App Store.',
            'Produced technical studies and architecture designs for multi-domain client products.',
            'Developed Android and iOS features with strong focus on performance, code quality, and maintainability.',
            'Implemented unit, integration, and UI test strategies to improve release confidence.',
            'Participated in technical migrations and modernization of existing mobile codebases.',
            'Worked in Agile (Scrum/Kanban) environments while contributing to team rituals and continuous improvement.',
          ],
        },
      ],
    },
    services: {
      heading: 'Expertise & Services',
      detailsLabel: 'Details',
      items: [
        {
          title: 'Android & iOS Product Delivery',
          description:
            'I build production-grade mobile applications from scratch or through strategic refactors, with a strong focus on product quality and long-term maintainability.',
          tech: ['Flutter', 'Android', 'iOS', 'Kotlin', 'Swift'],
        },
        {
          title: 'Mobile Architecture Modernization',
          description:
            'I help teams modernize existing apps with durable architecture foundations, clear module boundaries, and patterns adapted to scale and team velocity.',
          tech: ['Clean Architecture', 'MVP', 'MVVM', 'MVI', 'KMP'],
        },
        {
          title: 'Backend For Frontend (BFF)',
          description:
            'I design and implement BFF layers tailored to mobile use cases, reducing client complexity and improving reliability, security, and release speed.',
          tech: ['Ktor', 'Node.js', 'TypeScript', 'REST APIs'],
        },
        {
          title: 'CI/CD & Release Automation',
          description:
            'I set up and optimize mobile CI/CD pipelines so teams can ship faster with confidence, repeatability, and strong release governance.',
          tech: ['GitHub Actions', 'Jenkins', 'Fastlane', 'Docker'],
        },
        {
          title: 'Quality, Stability & Security',
          description:
            'I improve engineering quality with robust test strategies, observability practices, and security-first implementation standards across critical user journeys.',
          tech: ['Unit Testing', 'Integration Testing', 'Firebase', 'Crash Analytics'],
        },
        {
          title: 'Technical Leadership & Delivery',
          description:
            'I support teams with technical framing, effort estimation, and pragmatic delivery leadership to move from MVP to scalable, production-ready products.',
          tech: ['Agile', 'Scrum', 'Kanban', 'Product Collaboration'],
        },
      ],
    },
    cv: {
      heading: 'CV',
      description:
        'Download my latest CV in English.',
      title: 'English CV',
      subtitle: 'Senior Mobile Engineer',
      downloadCta: 'Download CV (EN)',
      file: {
        url: '/cv/Hedi_Hidouri_CV_EN.pdf',
        downloadName: 'Hedi_Hidouri_CV_EN.pdf',
      },
    },
    contact: {
      overline: 'Let\'s Build Something Solid',
      title: 'Available for freelance missions',
      description:
        'I partner with product teams to deliver robust mobile applications, streamline architecture, and accelerate release delivery. If you need senior engineering support for Android, iOS, Flutter, or mobile platform strategy, let\'s talk.',
      cta: 'Start a Conversation',
    },
    footer: {
      builtBy: 'Built by Hedi Hidouri',
    },
  },

  fr: {
    head: {
      title: 'Hedi Hidouri | Ingenieur Mobile Senior',
      description:
        'Ingenieur Mobile Senior specialise iOS, Android, Flutter, KMP et services backend Ktor.',
      locale: 'fr_FR',
      keywords: [
        'Ingenieur Mobile Senior',
        'Ingenieur identite numerique',
        'Developpeur Flutter',
        'Developpeur Android',
        'Developpeur iOS',
        'Kotlin Multiplatform',
        'EUDI',
        'EUDIW',
        'OIDC4VCI',
        'OIDC4VP',
        'OpenID4VCI',
        'OpenID4VP',
        'W3C Verifiable Credentials',
        'eIDAS 2.0',
        'Verification d age',
        'OpenAPI',
        'Python',
        'Docker',
        'Architecture mobile',
        'BFF',
        'CI/CD mobile',
        'Freelance mobile Paris',
      ],
    },
    layout: {
      skipToContent: 'Aller au contenu',
      whatsAppCta: 'WhatsApp',
      whatsAppAriaLabel: 'Ouvrir le chat WhatsApp',
    },
    nav: {
      homeAriaLabel: 'Accueil',
      menuButtonAriaLabel: 'Menu',
      languageSwitcherAriaLabel: 'Choisir la langue',
      links: [
        { name: 'A propos', url: '#about' },
        { name: 'Experience', url: '#jobs' },
        { name: 'Expertises', url: '#services' },
        { name: 'CV', url: '#cv' },
        { name: 'Contact', url: '#contact' },
      ],
    },
    hero: {
      intro: 'Bonjour, je suis',
      title: 'Hedi Hidouri.',
      subtitle: 'Ingenieur Mobile Senior.',
      description:
        'Je conçois, développe et modernise des produits mobiles robustes pour des équipes tech, fintech et e-commerce. Mon objectif est simple: sécuriser la livraison Android/iOS, structurer une architecture durable et exécuter avec fiabilité du MVP à l\'échelle.',
      quickStats: [
        { label: 'Localisation', value: 'Paris, France' },
        { label: 'Experience', value: '8+ ans' },
      ],
      cta: 'Travaillons Ensemble',
    },
    about: {
      heading: 'A propos',
      paragraphs: [
        'J\'accompagne des entreprises tech, fintech et e-commerce dans la conception, le développement et la modernisation d\'applications mobiles fiables, performantes et pensées pour durer.',
        'Sur des produits à forte volumétrie et à fortes contraintes (logistique, paiement, identité numérique, mobilité, banque), j\'aide les équipes à livrer des applications Android/iOS robustes, à sécuriser les parcours critiques et à structurer une architecture mobile + backend maintenable.',
      ],
      valueTitle: 'Ma valeur ajoutee',
      valueList: [
        'Une vision end-to-end: mobile, backend (BFF), architecture, CI/CD.',
        'Une forte expertise sur les systèmes sensibles (sécurité, conformité, performance).',
        'Une capacité à cadrer un produit techniquement du MVP jusqu aux plateformes à grande échelle.',
        'Une approche pragmatique et orientée delivery: ce qui compte, c est ce qui sort en production.',
      ],
      techIntro: 'Technologies clés utilisées régulièrement:',
      skills: sharedSkills,
      snapshotTitle: 'Profil en bref',
      snapshotStats: [
        { label: 'Role', value: 'Ingenieur Mobile Senior' },
        { label: 'Localisation', value: 'Paris, France (hybride / remote)' },
        { label: 'Langues', value: 'Francais, Anglais' },
        {
          label: 'Environnements preferes',
          value: 'Equipes produit, delivery Agile, plateformes mobiles a fort impact',
        },
      ],
      domains: [
        'Banque & services financiers',
        'E-commerce',
        'Grande distribution',
        'Plateformes digitales',
        'Services numeriques',
      ],
    },
    jobs: {
      heading: 'Experiences Professionnelles',
      tabsAriaLabel: 'Onglets experiences',
      items: [
        {
          title: 'Ingenieur Mobile Senior',
          company: 'INGroupe',
          location: 'Paris, France',
          range: 'avr. 2025 - Aujourd hui',
          url: 'https://ingroupe.com/',
          points: [
            'Développement d\'un wallet d\'identité numérique cross-platform aligné avec l\'architecture de référence EUDI wallet sur Android (Kotlin, Jetpack Compose) et iOS (Swift, SwiftUI).',
            'Bootstrapping de l\'application from scratch avec une Clean Architecture modulaire, un pattern MVI et des flux sécurisés de gestion d\'identité.',
            'Conception et implémentation d\'un SDK d\'identité nationale: provisioning de credentials via NFC (ISO-DEP) et QR code, dérivation/authentification via X.509, PKCS#12 et HOTP.',
            'Conception et développement de services backend issuer pour l\'émission de credentials via OpenID4VCI, avec des contrats API sécurisés et alignés sur les standards.',
            'Implémentation des protocoles de credentials compatibles EUDI: OIDC4VCI/OpenID4VCI (issuance), OIDC4VP/OpenID4VP (presentation), et SD-JWT pour la divulgation sélective et la validation de signature.',
            'Développement et évolution de services BFF pour simplifier l\'intégration mobile, orchestrer les flux identité/paiement et réduire la complexité côté client.',
            'Delivery de cas d\'usage de vérification d\'âge et de badging digital, dont Badgemeup, une application d\'émission de badges et credentials conforme EUDI.',
            'Mise en place et maintenance des pipelines CI/CD mobile avec Bitbucket, Jenkins, Fastlane, Docker et des garde-fous qualité automatisés (JUnit, XCTest, MockK).',
            'Livraison de workflows end-to-end de partage d\'identité alignés avec eIDAS 2.0, avec contribution active aux décisions d\'architecture, à la coordination transverse et au mentoring technique.',
          ],
        },
        {
          title: 'Ingenieur Mobile Senior',
          company: 'Stuart',
          location: 'Paris, France',
          range: 'nov. 2022 - avr. 2025',
          url: 'https://stuart.com/',
          points: [
            'Pilotage de la modernisation des parcours Android et iOS avec Flutter pour une plateforme logistique à grande échelle.',
            'Développement et supervision d\'un Backend For Frontend mobile en Node.js avec des endpoints adaptés aux besoins applicatifs.',
            'Orchestration de composants backend avec Docker et Kafka pour améliorer la fiabilité des échanges entre services et applications.',
            'Structuration et industrialisation de pipelines CI/CD via GitHub Actions et Fastlane pour automatiser builds et déploiements.',
            'Renforcement de la qualité logicielle grâce aux tests unitaires et d intégration dans un contexte Agile.',
            'Contribution à des domaines fonctionnels clés: optimisation de la livraison, suivi des courses et évolution des paiements.',
          ],
        },
        {
          title: 'Consultant en Developpement Mobile',
          company: 'Carrefour',
          location: 'Paris, France',
          range: 'mai 2021 - nov. 2022',
          url: 'https://www.carrefour.com/',
          points: [
            'Contribution à l évolution à grande échelle des applications Android Carrefour Drive et Livraison.',
            'Développement de nouvelles fonctionnalités et refontes UI/UX pour améliorer la fluidité, la performance et l expérience client.',
            'Modernisation de l architecture via MVI et Clean Architecture avec des stacks réactives (Flow, Coroutines).',
            'Mise en place de pratiques de modularisation et d injection de dépendances avec Hilt pour améliorer la maintenabilité.',
            'Optimisation de la CI mobile via Jenkins et Gradle Kotlin DSL (KTS).',
            'Renforcement de la fiabilité grâce aux tests unitaires et d intégration, en collaboration étroite avec les équipes produit et design.',
          ],
        },
        {
          title: 'Developpeur Mobile Android / iOS',
          company: 'JCDecaux',
          location: 'Paris, France',
          range: 'janv. 2021 - mai 2021',
          url: 'https://www.jcdecaux.com/',
          points: [
            'Développement et amélioration continue d applications mobiles de vélos en libre-service utilisées par plus d un million d utilisateurs.',
            'Implémentation de nouvelles fonctionnalités Android et iOS avec optimisation continue des performances globales.',
            'Réduction des incidents en production via des actions ciblées de stabilisation et de correction.',
            'Mise en place de tests unitaires et d intégration Android/iOS avec une couverture solide.',
            'Collaboration étroite avec les équipes produit pour clarifier les besoins métier et améliorer l expérience utilisateur.',
            'Delivery en cycles Agile/Scrum avec implication active sur la planification et l exécution.',
          ],
        },
        {
          title: 'Ingenieur Mobile iOS',
          company: 'HSBC',
          location: 'Paris, France',
          range: 'mars 2019 - dec. 2020',
          url: 'https://www.hsbc.com/',
          points: [
            'Amélioration continue de l application bancaire iOS avec un fort focus sur l expérience utilisateur et les fonctionnalités clés.',
            'Implémentation de fonctionnalités de sécurité avancées, incluant biométrie et provisioning wallet.',
            'Traduction des besoins métier et conformité en solutions techniques scalables.',
            'Réalisation de tests unitaires et d intégration, avec support des phases de recette interne.',
            'Investigation et correction d incidents en production pour améliorer la stabilité et la qualité de service.',
            'Contribution à l enrichissement des librairies internes, à la documentation technique et au partage de bonnes pratiques.',
          ],
        },
        {
          title: 'Ingenieur Developpeur Mobile',
          company: 'Bulldozer',
          location: 'Ariana, Tunisie',
          range: 'juil. 2017 - fev. 2019',
          url: 'https://www.linkedin.com/company/bulldozer/',
          points: [
            'Contribution au cycle de vie complet des applications mobiles: planification, développement, tests et déploiement sur stores.',
            'Réalisation d études techniques et de conceptions d architecture pour des produits clients multi-domaines.',
            'Développement de fonctionnalités Android et iOS avec une attention forte à la performance, à la qualité et à la maintenabilité.',
            'Mise en place de stratégies de tests unitaires, d intégration et UI pour renforcer la confiance en release.',
            'Participation aux migrations techniques et à la modernisation de bases applicatives existantes.',
            'Travail dans des environnements Agile (Scrum/Kanban) avec contribution active aux rituels et à l amélioration continue.',
          ],
        },
      ],
    },
    services: {
      heading: 'Expertises & Services',
      detailsLabel: 'Details',
      items: [
        {
          title: 'Delivery Produit Android & iOS',
          description:
            'Je développe des applications mobiles prêtes pour la production, en création from scratch ou via des refontes ciblées, avec un focus fort sur la qualité produit et la maintenabilité long terme.',
          tech: ['Flutter', 'Android', 'iOS', 'Kotlin', 'Swift'],
        },
        {
          title: 'Modernisation d Architecture Mobile',
          description:
            'J aide les équipes à moderniser des applications existantes avec des fondations durables, des frontières de modules claires et des patterns adaptés à l échelle et à la vélocité équipe.',
          tech: ['Clean Architecture', 'MVP', 'MVVM', 'MVI', 'KMP'],
        },
        {
          title: 'Backend For Frontend (BFF)',
          description:
            'Je conçois et implémente des couches BFF orientées usages mobiles pour réduire la complexité côté client et améliorer fiabilité, sécurité et vitesse de livraison.',
          tech: ['Ktor', 'Node.js', 'TypeScript', 'APIs REST'],
        },
        {
          title: 'CI/CD & Automatisation des Releases',
          description:
            'Je mets en place et optimise des pipelines CI/CD mobile pour livrer plus vite avec confiance, répétabilité et une gouvernance de release solide.',
          tech: ['GitHub Actions', 'Jenkins', 'Fastlane', 'Docker'],
        },
        {
          title: 'Qualite, Stabilite & Securite',
          description:
            'J améliore la qualité engineering avec des stratégies de test robustes, de bonnes pratiques d observabilité et des standards d implémentation security-first sur les parcours critiques.',
          tech: ['Tests unitaires', 'Tests integration', 'Firebase', 'Crash Analytics'],
        },
        {
          title: 'Leadership Technique & Delivery',
          description:
            'J accompagne les équipes sur le cadrage technique, l estimation et le pilotage pragmatique du delivery pour passer du MVP à des produits scalables et prêts production.',
          tech: ['Agile', 'Scrum', 'Kanban', 'Collaboration produit'],
        },
      ],
    },
    cv: {
      heading: 'CV',
      description:
        'Téléchargez mon CV à jour en français.',
      title: 'CV Français',
      subtitle: 'Ingénieur Mobile Senior',
      downloadCta: 'Télécharger le CV (FR)',
      file: {
        url: '/cv/Hedi_Hidouri_CV_FR.pdf',
        downloadName: 'Hedi_Hidouri_CV_FR.pdf',
      },
    },
    contact: {
      overline: 'Construisons quelque chose de solide',
      title: 'Disponible pour des missions freelance',
      description:
        'J accompagne des équipes produit pour livrer des applications mobiles robustes, clarifier l architecture et accélérer les releases. Si vous avez besoin d un support senior sur Android, iOS, Flutter ou la stratégie plateforme mobile, échangeons.',
      cta: 'Démarrer une discussion',
    },
    footer: {
      builtBy: 'Concu par Hedi Hidouri',
    },
  },
};
